import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Gift, ShoppingCart, Store, Repeat, HandHeart, Wrench, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { logger } from "@/lib/logger";
import { validateDoc, formatDoc } from "@/lib/docs";

const REFERRAL_KEY = "rivendas_referrer";

// Intent -> {role stored on backend, post-signup redirect}
// buyer/seller are stored on the user profile as usual. "doar", "trocar", "servico"
// still create a buyer account (they PUBLISH the order) and jump straight
// into the CreateOrder flow pre-selected with the right type.
const INTENTS = [
  {
    v: "buyer",
    role: "buyer",
    label: "Quero comprar",
    icon: ShoppingCart,
    desc: "Publico o que quero e fornecedores respondem",
    redirect: "/",
  },
  {
    v: "seller",
    role: "seller",
    label: "Quero vender/atender",
    icon: Store,
    desc: "Recebo oportunidades que combinam comigo",
    redirect: "/especialidades",
  },
  {
    v: "servico",
    role: "buyer",
    label: "Quero contratar serviço",
    icon: Wrench,
    desc: "Preciso de um profissional",
    redirect: "/create?type=servico",
  },
  {
    v: "trocar",
    role: "buyer",
    label: "Quero trocar",
    icon: Repeat,
    desc: "Permuto meu item por outro sem envolver dinheiro",
    redirect: "/create?type=permuta",
  },
  {
    v: "doar",
    role: "buyer",
    label: "Quero doar",
    icon: Gift,
    desc: "Ofereço algo gratuitamente para quem precisa",
    redirect: "/create?type=doacao&direction=dar",
  },
];

function normalizeIntent(raw) {
  if (raw === "seller") return "seller";
  if (raw === "servico" || raw === "service" || raw === "servicos") return "servico";
  if (raw === "doar" || raw === "doacao" || raw === "donation") return "doar";
  if (raw === "trocar" || raw === "permuta" || raw === "trade") return "trocar";
  return "buyer";
}

// Turn a FastAPI error `detail` (string OR list of {msg}) into a user-facing
// message. Prevents "Objects are not valid as a React child" crashes.
function formatRegisterError(detail) {
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || String(x)).join("; ");
  if (typeof detail === "string") return detail;
  return "Erro ao cadastrar. Verifique se o e-mail já existe.";
}

export default function Register() {
  const { register } = useAuth();
  const [sp] = useSearchParams();
  // Accept legacy ?role= and new ?intent=
  const initialIntent = normalizeIntent(sp.get("intent") || sp.get("role") || "buyer");
  const [intent, setIntent] = useState(initialIntent);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    doc_type: "cpf",
    doc_number: "",
    city: "",
  });
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(REFERRAL_KEY);
      if (raw) {
        const r = JSON.parse(raw);
        if (r?.user_id && Date.now() - (r.ts || 0) < 30 * 24 * 60 * 60 * 1000) {
          setReferrer(r);
        }
      }
    } catch (err) {
      logger.warn("register: unable to read referral", err?.message);
    }
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const current = INTENTS.find((i) => i.v === intent) || INTENTS[0];

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignup = () => {
    // Persist the chosen intent so AuthCallback can honor the post-signup redirect
    try {
      sessionStorage.setItem("rivendas_signup_intent", intent);
    } catch (err) {
      logger.warn("register: unable to persist intent", err?.message);
    }
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    // Client-side sanity checks with clear messages
    if (form.name.trim().length < 2) {
      toast.error("Nome deve ter pelo menos 2 caracteres.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error("As senhas não coincidem. Digite a mesma senha nos dois campos.");
      return;
    }
    // If a doc is provided, it must be valid (empty = skip, backend enforces too)
    if (intent === "seller" && form.doc_number && !validateDoc(form.doc_type, form.doc_number)) {
      toast.error(form.doc_type === "cpf" ? "CPF inválido" : "CNPJ inválido");
      return;
    }
    setLoading(true);
    try {
      // Backend does NOT accept confirm_password — strip it before sending
      const { confirm_password: _cp, ...payload } = form;
      payload.role = current.role;
      if (referrer?.user_id) payload.referred_by = referrer.user_id;
      await register(payload);
      try { localStorage.removeItem(REFERRAL_KEY); } catch (err) { logger.warn("register: cleanup", err?.message); }

      const msg = {
        buyer: "Conta criada! Bem-vindo(a) ao Rivendas",
        seller: "Conta criada! Configure suas especialidades para receber só o que combina",
        servico: "Conta criada! Vamos publicar seu pedido de serviço 🛠",
        doar: "Conta criada! Vamos publicar sua doação 🎁",
        trocar: "Conta criada! Vamos publicar sua troca 🔄",
      }[intent];
      toast.success(referrer ? "Conta criada! 🎁 Seu 1º pedido virá em destaque." : msg);
      nav(current.redirect, { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      const msgErr = formatRegisterError(d);
      toast.error(msgErr);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <span className="inline-block w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-heading font-black text-xl">R</span>
          </span>
          <span className="font-heading font-black text-xl">
            <span className="text-primary">Ri</span>vendas
          </span>
        </Link>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
          <h1 className="font-heading font-black text-3xl tracking-tight">Criar conta</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comprar, vender, trocar ou doar — em segundos.
          </p>

          {referrer && (
            <div
              data-testid="referral-banner"
              className="mt-4 bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-xl p-3 flex items-center gap-3"
            >
              <HandHeart size={22} className="text-primary shrink-0" />
              <div className="text-sm">
                <p className="font-bold">
                  Convidado(a) por <span className="text-primary">{referrer.name?.split(" ")[0]}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Seu 1º pedido vai em destaque no feed 🎁
                </p>
              </div>
            </div>
          )}

          {/* Google sign-in — one-click, no password */}
          <button
            type="button"
            data-testid="register-google"
            onClick={googleSignup}
            className="mt-6 w-full h-12 rounded-lg border border-border bg-card hover:bg-secondary transition-colors flex items-center justify-center gap-2 text-sm font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Cadastrar com Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">ou com e-mail</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Intent picker — 5 cards on par */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">O que você quer fazer?</Label>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2">
              {INTENTS.map((r) => {
                const I = r.icon;
                const active = intent === r.v;
                return (
                  <button
                    key={r.v}
                    type="button"
                    data-testid={`intent-${r.v}`}
                    onClick={() => setIntent(r.v)}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${
                      active
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50 bg-card"
                    }`}
                  >
                    <I size={18} className={active ? "text-primary" : "text-muted-foreground"} />
                    <p className={`font-heading font-bold text-sm mt-2 ${active ? "text-primary" : ""}`}>
                      {r.label}
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-tight mt-1">{r.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} noValidate className="space-y-4 mt-6">
            <div>
              <Label htmlFor="name">Nome {intent === "seller" ? "ou razão social" : "completo"}</Label>
              <Input
                id="name"
                data-testid="register-name"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                minLength={2}
                className="h-12"
              />
            </div>
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                data-testid="register-email"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                className="h-12"
              />
            </div>
            <div>
              <Label htmlFor="password">Senha (mín. 6)</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="register-password"
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  required
                  minLength={6}
                  className="h-12 pr-11"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirmar senha</Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  data-testid="register-confirm-password"
                  type={showConfirm ? "text" : "password"}
                  value={form.confirm_password}
                  onChange={(e) => set("confirm_password", e.target.value)}
                  required
                  minLength={6}
                  className={`h-12 pr-11 ${
                    form.confirm_password && form.confirm_password !== form.password
                      ? "border-destructive focus-visible:ring-destructive"
                      : ""
                  }`}
                  autoComplete="new-password"
                  placeholder="Digite a mesma senha"
                />
                <button
                  type="button"
                  data-testid="toggle-confirm-password"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {form.confirm_password && form.confirm_password !== form.password && (
                <p data-testid="password-mismatch" className="text-xs text-destructive mt-1">
                  As senhas não coincidem.
                </p>
              )}
            </div>
            {intent === "seller" && (
              <div className="space-y-3">
                <div>
                  <Label>Tipo de cadastro</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {[
                      { v: "cpf", l: "PF (CPF)" },
                      { v: "mei", l: "MEI" },
                      { v: "cnpj", l: "CNPJ" },
                    ].map((d) => (
                      <button
                        key={d.v}
                        type="button"
                        data-testid={`doctype-${d.v}`}
                        onClick={() => set("doc_type", d.v)}
                        className={`h-10 rounded-lg border text-sm font-medium transition-colors ${
                          form.doc_type === d.v
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        {d.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="doc_number" className="flex items-center gap-2">
                    {form.doc_type === "cpf" ? "CPF" : "CNPJ"} <span className="text-xs text-muted-foreground font-normal">(opcional — vira selo Verificado)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="doc_number"
                      data-testid="register-doc-number"
                      inputMode="numeric"
                      value={form.doc_number}
                      onChange={(e) => set("doc_number", formatDoc(form.doc_type, e.target.value))}
                      placeholder={form.doc_type === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                      className={`h-12 pr-10 ${
                        form.doc_number && !validateDoc(form.doc_type, form.doc_number)
                          ? "border-destructive focus-visible:ring-destructive"
                          : ""
                      }`}
                    />
                    {form.doc_number && validateDoc(form.doc_type, form.doc_number) && (
                      <ShieldCheck size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500" />
                    )}
                  </div>
                  {form.doc_number && !validateDoc(form.doc_type, form.doc_number) && (
                    <p data-testid="doc-invalid" className="text-xs text-destructive mt-1">
                      {form.doc_type === "cpf" ? "CPF" : "CNPJ"} inválido
                    </p>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="city">Cidade</Label>
              <Input
                id="city"
                data-testid="register-city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="Ex: São Paulo"
                className="h-12"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full h-12 text-base font-bold"
            >
              {loading ? "Criando..." : "Criar conta"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            Já tem conta?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="goto-login">
              Entrar
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
